'use client';

import { useEffect, useState, useRef } from 'react';
import { X, Calendar, Clock, TrendingUp, Shield, Zap, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PulsePayload, PulseRow, PulseCategory } from '@/lib/cockpit/pulse-generator';

// ============================================================================
// Types
// ============================================================================

interface PulseSheetProps {
  isOpen: boolean;
  onClose: () => void;
  pulseData: PulsePayload | null;
  isLoading?: boolean;
  error?: string | null;
  isDemo?: boolean;
}

// ============================================================================
// Category Icons and Colors
// ============================================================================

const getCategoryIcon = (category: PulseCategory) => {
  switch (category) {
    case 'expiring_opportunity':
      return Clock;
    case 'new_opportunity':
    case 'updated_opportunity':
      return TrendingUp;
    case 'portfolio_delta':
      return Zap;
    case 'guardian_delta':
      return Shield;
    case 'proof_receipt':
      return FileText;
    default:
      return Calendar;
  }
};

const getCategoryColor = (category: PulseCategory) => {
  switch (category) {
    case 'expiring_opportunity':
      return 'text-red-400';
    case 'new_opportunity':
      return 'text-green-400';
    case 'updated_opportunity':
      return 'text-blue-400';
    case 'portfolio_delta':
      return 'text-yellow-400';
    case 'guardian_delta':
      return 'text-purple-400';
    case 'proof_receipt':
      return 'text-gray-400';
    default:
      return 'text-gray-400';
  }
};

// ============================================================================
// Demo Data
// ============================================================================

const getDemoPulseData = (): PulsePayload => ({
  pulse_date: new Date().toISOString().split('T')[0],
  timezone: 'America/New_York',
  rows: [
    {
      kind: 'expiring_opportunity',
      title: 'Arbitrum quest ends in 8h',
      chip: '8h left',
      cta: { label: 'Open', href: '#demo' },
      provenance: 'confirmed',
      event_time: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      priority: -8,
    },
    {
      kind: 'new_opportunity',
      title: 'New DeFi yield opportunity detected',
      chip: 'New',
      cta: { label: 'View', href: '#demo' },
      provenance: 'simulated',
      event_time: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
      priority: Date.now() - 1 * 60 * 60 * 1000,
    },
    {
      kind: 'portfolio_delta',
      title: 'ETH position increased by 15%',
      cta: { label: 'View', href: '#demo' },
      provenance: 'confirmed',
      event_time: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      priority: 2,
    },
    {
      kind: 'guardian_delta',
      title: 'New approval detected: Uniswap V3',
      chip: 'High',
      cta: { label: 'Review', href: '#demo' },
      provenance: 'confirmed',
      event_time: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
      priority: 2,
    },
  ],
  generated_at: new Date().toISOString(),
});

// ============================================================================
// Pulse Row Component
// ============================================================================

interface PulseRowComponentProps {
  row: PulseRow;
  isDemo?: boolean;
  onClick: () => void;
}

const PulseRowComponent = ({ row, isDemo, onClick }: PulseRowComponentProps) => {
  const Icon = getCategoryIcon(row.kind);
  const iconColor = getCategoryColor(row.kind);
  
  const formatEventTime = (eventTime: string) => {
    const date = new Date(eventTime);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return `${diffDays}d ago`;
    } else if (diffHours > 0) {
      return `${diffHours}h ago`;
    } else {
      return 'Just now';
    }
  };

  const handleClick = () => {
    if (isDemo) {
      // In demo mode, show tooltip or do nothing
      return;
    }
    onClick();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <motion.div
      className={`
        group relative rounded-[24px] border border-white/8
        bg-white/[0.03] p-4 backdrop-blur-sm hover:bg-white/[0.06]
        transition-all duration-200 cursor-pointer
        ${isDemo ? 'opacity-75' : ''}
      `}
      whileHover={{ scale: 1.01, y: -1 }}
      transition={{ duration: 0.15 }}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`${row.title} - ${row.cta.label}`}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`flex-shrink-0 ${iconColor}`}>
          <Icon size={20} />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-white font-medium text-sm leading-tight">
              {row.title}
            </h3>
            
            {/* Chip */}
            {row.chip && (
              <span className={`
                flex-shrink-0 rounded-full px-2 py-1 text-xs font-medium
                ${row.kind === 'expiring_opportunity' 
                  ? 'border border-red-500/30 bg-red-500/15 text-red-200'
                  : row.kind === 'new_opportunity'
                  ? 'border border-emerald-500/30 bg-emerald-500/15 text-emerald-200'
                  : 'border border-[#7ea3f2]/30 bg-[#7ea3f2]/12 text-[#dbe4ff]'
                }
              `}>
                {row.chip}
              </span>
            )}
          </div>
          
          {/* Metadata */}
          <div className="mt-2 flex items-center gap-4 text-xs text-[#8f8a82]">
            <span>{formatEventTime(row.event_time)}</span>
            <span className="capitalize">{row.provenance}</span>
          </div>
        </div>
      </div>
      
      {/* Demo overlay */}
      {isDemo && (
        <div className="absolute inset-0 flex items-center justify-center rounded-[24px] bg-black/25 opacity-0 transition-opacity group-hover:opacity-100">
          <span className="rounded-full bg-black/60 px-2 py-1 text-xs text-white">
            Demo
          </span>
        </div>
      )}
    </motion.div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const PulseSheet = ({ 
  isOpen, 
  onClose, 
  pulseData, 
  isLoading = false, 
  error = null,
  isDemo = false 
}: PulseSheetProps) => {
  const [focusedElementBeforeOpen, setFocusedElementBeforeOpen] = useState<HTMLElement | null>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Store focus before opening and restore on close
  useEffect(() => {
    if (isOpen) {
      setFocusedElementBeforeOpen(document.activeElement as HTMLElement);
      // Focus the close button when sheet opens
      setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 100);
    } else if (focusedElementBeforeOpen) {
      // Restore focus to the element that opened the sheet
      focusedElementBeforeOpen.focus();
      setFocusedElementBeforeOpen(null);
    }
  }, [isOpen, focusedElementBeforeOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Focus trap
  useEffect(() => {
    if (!isOpen || !sheetRef.current) return;

    const sheet = sheetRef.current;
    const focusableElements = sheet.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, [isOpen]);

  // Handle swipe down on mobile (simplified)
  useEffect(() => {
    if (!isOpen || !sheetRef.current) return;

    let startY = 0;
    let currentY = 0;
    let isDragging = false;

    const handleTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
      isDragging = true;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;
      currentY = e.touches[0].clientY;
      const deltaY = currentY - startY;
      
      // Only allow downward swipes and only if at the top of the sheet
      if (deltaY > 0 && sheetRef.current?.scrollTop === 0) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = () => {
      if (!isDragging) return;
      isDragging = false;
      
      const deltaY = currentY - startY;
      // Close if swiped down more than 100px
      if (deltaY > 100) {
        onClose();
      }
    };

    const sheet = sheetRef.current;
    sheet.addEventListener('touchstart', handleTouchStart, { passive: false });
    sheet.addEventListener('touchmove', handleTouchMove, { passive: false });
    sheet.addEventListener('touchend', handleTouchEnd);

    return () => {
      sheet.removeEventListener('touchstart', handleTouchStart);
      sheet.removeEventListener('touchmove', handleTouchMove);
      sheet.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isOpen, onClose]);

  // Get pulse data (demo or real)
  // If pulseData is null, fall back to demo data regardless of isDemo flag
  const displayData = (isDemo || pulseData === null) ? getDemoPulseData() : pulseData;

  const handleRowClick = (row: PulseRow) => {
    if (isDemo) return;
    
    // Navigate to the row's href
    if (row.cta.href.startsWith('#')) {
      // Handle hash navigation
      window.location.hash = row.cta.href.substring(1);
    } else {
      // Handle regular navigation
      window.location.href = row.cta.href;
    }
  };

  const formatPulseDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-black/92 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          <motion.div
            ref={sheetRef}
            className="fixed inset-0 z-50 overflow-y-auto"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ type: 'spring', damping: 28, stiffness: 260 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="pulse-sheet-title"
          >
            <div className="min-h-full px-4 py-6 sm:px-6 lg:px-8">
              <div className="mx-auto max-w-[1180px]">
                <div className="overflow-hidden rounded-[34px] border border-white/10 bg-[#0b0b0c] shadow-[0_30px_120px_rgba(0,0,0,0.6)]">
                  <div className="sticky top-0 z-10 border-b border-white/8 bg-[#0b0b0c]/95 px-5 py-5 backdrop-blur-sm sm:px-7">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.28em] text-[#8f8a82]">Daily pulse</p>
                        <h1
                          id="pulse-sheet-title"
                          className="mt-3 text-3xl text-[#f6f2ea] sm:text-4xl"
                          style={{ fontFamily: 'Iowan Old Style, Georgia, serif' }}
                        >
                          Today&apos;s pulse
                        </h1>
                        {displayData ? (
                          <p className="mt-2 text-sm text-[#9c978f]">
                            {formatPulseDate(displayData.pulse_date)}
                          </p>
                        ) : null}
                      </div>

                      <button
                        ref={closeButtonRef}
                        onClick={onClose}
                        className="rounded-full border border-white/10 bg-white/[0.03] p-3 text-[#9c978f] transition-colors hover:bg-white/[0.08] hover:text-[#f6f2ea]"
                        aria-label="Close pulse sheet"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  </div>

                  <div className="p-5 pb-8 sm:p-7">
                    {displayData ? (
                      <div className="mb-6 grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
                        <div className="rounded-[28px] border border-white/8 bg-[linear-gradient(180deg,rgba(126,163,242,0.16),rgba(255,255,255,0.02))] p-5">
                          <p className="text-[11px] uppercase tracking-[0.28em] text-[#dbe4ff]">Opening read</p>
                          <p
                            className="mt-4 text-3xl text-[#f6f2ea]"
                            style={{ fontFamily: 'Iowan Old Style, Georgia, serif' }}
                          >
                            {displayData.rows.length} items in motion
                          </p>
                          <p className="mt-3 max-w-2xl text-sm leading-6 text-[#b8b2a7]">
                            A calmer read on what changed today across opportunities, portfolio drift, and Guardian events.
                          </p>
                        </div>

                        <div className="rounded-[28px] border border-white/8 bg-white/[0.02] p-5">
                          <p className="text-[11px] uppercase tracking-[0.28em] text-[#8f8a82]">Coverage</p>
                          <div className="mt-4 space-y-3 text-sm text-[#b8b2a7]">
                            <div className="flex items-center justify-between border-b border-white/8 pb-3">
                              <span>Rows loaded</span>
                              <span className="text-[#f6f2ea]">{displayData.rows.length}</span>
                            </div>
                            <div className="flex items-center justify-between border-b border-white/8 pb-3">
                              <span>Source</span>
                              <span className="text-[#f6f2ea]">{isDemo ? 'Demo' : 'Live'}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span>Generated</span>
                              <span className="text-[#f6f2ea]">
                                {new Date(displayData.generated_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}

              {/* Demo Mode Banner */}
              {isDemo && (
                <div className="mb-6 rounded-[24px] border border-[#7ea3f2]/25 bg-[#7ea3f2]/12 p-4">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-[#a7c0ff]"></div>
                    <span className="text-sm font-medium text-[#dbe4ff]">Demo Mode</span>
                  </div>
                  <p className="mt-1 text-xs text-[#bfd0ff]">
                    This is sample pulse data. Connect your wallet to see real insights.
                  </p>
                </div>
              )}

              {/* Loading State */}
              {isLoading && (
                <div className="space-y-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="h-24 rounded-[24px] border border-white/8 bg-white/[0.03]"></div>
                    </div>
                  ))}
                </div>
              )}

              {/* Error State */}
              {error && !isLoading && (
                <div className="py-10 text-center">
                  <div className="mb-2 text-red-400">Failed to load pulse data</div>
                  <div className="text-sm text-[#9c978f]">{error}</div>
                </div>
              )}

              {/* Empty State */}
              {!isLoading && !error && displayData && displayData.rows.length === 0 && (
                <div className="py-12 text-center">
                  <Calendar size={48} className="mx-auto mb-4 text-[#6f6a62]" />
                  <h3 className="mb-2 text-lg font-medium text-white">Quiet Day</h3>
                  <p className="mb-6 text-[#9c978f]">
                    No new activity since your last visit. Check back later for updates.
                  </p>
                  <div className="space-y-2">
                    <button
                      onClick={() => !isDemo && (window.location.href = '/hunter')}
                      className="block w-full px-4 py-2 text-sm text-[#a7c0ff] transition-colors hover:text-[#dbe4ff]"
                      disabled={isDemo}
                    >
                      Explore Hunter opportunities
                    </button>
                    <button
                      onClick={() => !isDemo && (window.location.href = '/alerts')}
                      className="block w-full px-4 py-2 text-sm text-[#a7c0ff] transition-colors hover:text-[#dbe4ff]"
                      disabled={isDemo}
                    >
                      Create alert rules
                    </button>
                  </div>
                </div>
              )}

              {/* Pulse Rows */}
              {!isLoading && !error && displayData && displayData.rows.length > 0 && (
                <div className="space-y-3">
                  {displayData.rows.map((row, index) => (
                    <PulseRowComponent
                      key={`${row.kind}-${index}`}
                      row={row}
                      isDemo={isDemo}
                      onClick={() => handleRowClick(row)}
                    />
                  ))}
                </div>
              )}

              {/* Footer */}
              {displayData && displayData.rows.length > 0 && (
                <div className="mt-8 border-t border-white/8 pt-6">
                  <p className="text-center text-xs text-[#6f6a62]">
                    Generated at {new Date(displayData.generated_at).toLocaleTimeString()}
                  </p>
                </div>
              )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
